export default {
    checklist: {
        answered_by: 'confirmed by {name}',
        title: 'Checklist',
        progress: '{answered}/{total} answered',
        legend_required: 'A yellow circle marks a required step; the room cannot be finished without it.',
        done: 'Done',
        not_applicable: 'Not applicable',
        fault: 'Fault',
        fault_title: 'Report a fault at this step',
        fault_note: 'What is wrong',
        fault_hint: 'Write what you saw — "bedside lamp does not work" is a work order, "fault" is not.',
        fault_save: 'Just record the fault',
        fault_and_issue: 'Record it and raise a work order',
        issue_section: 'Work order for maintenance',
        issue_photo: 'Photograph (optional)',
        issue_raised: 'A work order has been raised',
        issue_raised_message: 'Maintenance has been told',
        issue_failed: 'The work order could not be raised',
        photo_failed: 'The photograph could not be attached',
        incomplete: 'Answer the required steps of the list first.',
    },
    lost: {
        title: 'Lost property',
        hint: 'What a guest leaves behind. Write it down at once, with a photograph — "a blue scarf" matches half the scarves in the world and a picture does not.',
        add: 'Record a find',
        search: 'Search by description, number or guest',
        empty: 'Nothing recorded.',
        description: 'What it is',
        description_placeholder: 'For example: blue woollen scarf',
        room: 'Room',
        location: 'Where in the room',
        location_hint: 'This is what convinces a guest on the telephone that it is really theirs.',
        location_placeholder: 'For example: in the bedside drawer',
        storage: 'Where you put it',
        storage_hint: 'A register that does not say where the thing is has solved nothing.',
        photo: 'Photograph',
        guest: 'Guest that night: {name}',
        saved: 'The find has been recorded.',
        save_failed: 'The find could not be recorded.',
        photo_failed: 'The record was saved but the photograph could not be uploaded.',
        needs_description: 'Say what it is and pick a room.',
        load_failed: 'The list could not be loaded.',
        status_stored: 'In storage',
        status_contacted: 'Guest told',
        status_returned: 'Returned',
        status_shipped: 'Posted',
        status_disposed: 'Disposed of',
    },
    attention_list: {
        title: 'Needs attention',
        hint: 'The work and the room disagree. Go and look at the room, then put it right through its task.',
        room: 'Room {room}',
        // Quoted, because since v1.333.0 the two insertions can be phrases ("to
        // prepare"), not just adjectives — unquoted the sentence runs together.
        mismatch: 'The work says “{expected}”, the room says “{actual}”.',
        make_up_room_requested: 'The guest has asked for the room to be made up.',
        dnd_at_deadline: 'The do-not-disturb sign is still on the door, and the hour the room was promised for has passed.',
        unknown_state: 'unknown',
        settle: 'Settled',
        settled: 'Marked as looked at',
        settled_by_hand: 'Looked at by the supervisor',
        settle_failed: 'The row could not be closed',
    },
    round: {
        // Each person's round on one clock. The hours come from the API
        // (expected_start/_end) and are never computed here.
        title:          'When everybody finishes',
        summary:        'last room at {time} · {count} rounds',
        finishes:       'finishes at {time}',
        empty:          'No open work on anybody\'s round.',
        legend_working: 'working',
        legend_late:    'will not be ready in time',
        legend_now:     'now',
        // The rota is a plan and the morning is what it is: not a refusal, but the
        // fact a supervisor is after when she asks whether the house will be ready.
        after_shift:        'the round runs past the end of the shift',
        legend_after_shift: 'past the shift',
        dnd:                'do not disturb on the door',
        // The order a supervisor sets by dragging a block. A refusal is not a mistake:
        // the room was finished while the card was drawing the round, and the way on is
        // to read it again.
        reorder_saved:  'Order saved',
        reorder_stale:  'The round has changed since. We have reloaded it — try again.',
        reorder_failed: 'The order could not be saved.',
    },
    board: {
        // What the cell says about the round: who has it and when they reach it.
        // The hour comes from the API (expected_start) and is never computed here.
        unassigned:  'unassigned',
        norm:        'norm {value}',
        expected_at: 'comes up at {time}',
        awaiting_inspection: 'awaiting inspection',
        priority: 'do this first',
        // The guest's sign on the door, in the width a tile has — not the shouted
        // `live.*` wording, which is written for a row three times as wide. The legend
        // and the line above the floors use the same two words.
        dnd: 'DND',
        mur: 'Make up',
        // Under the tracks and only to a supervisor: a hint over every block would be a
        // tooltip nobody reads, and a hint shown to a housekeeper an instruction she
        // cannot follow.
        reorder_hint: 'Drag a room left or right to change the order',
        count_in_progress: 'Being cleaned',
        title: 'Room overview',
        ground: 'Ground floor',
        floor: 'Floor {floor}',
        areas: 'Common areas',
        working: 'somebody is in the room',
        in_room: 'In the room: {name}',
        deferred_overdue: 'deferred, past its hour',
        // The same words reception uses (`room_status.*`), lower case because they stand
        // in a legend and inside a sentence. Until v1.333.0 the two portals disagreed on
        // meaning as well: `out_of_order` read "v okvari" here and `out_of_service` read
        // "izven uporabe" — which is reception's name for `out_of_order`. One phrase for
        // two different codes is the most expensive kind of divergence, because nothing
        // shows it.
        status_clean: 'cleaned',
        status_dirty: 'to prepare',
        status_inspected: 'ready',
        status_occupied: 'occupied',
        status_out_of_order: 'out of order',
        status_out_of_service: 'out of service',
    },
    general: {
        hint: 'Hint',
        saved: 'Saved.',
        refreshed_now: 'refreshed just now',
        refreshed_ago: 'refreshed {value} ago',
        add:     'Add',
        edit:    'Edit',
        delete:  'Delete',
        cancel:  'Cancel',
        save:    'Save',
        close:   'Close',
        confirm: 'Confirm',
        select_confirm: 'Confirm',
        selected_count: 'selected: {count}',
        yes:     'Yes',
        no:      'No',
        back:    'Back',
        show_password: 'Show password',
        hide_password: 'Hide password',
        no_data: 'No data',
        loading: 'Loading …',
        today:   'Today',
        status:  'Status',
        refresh: 'Refresh',
        search:  'Search',
    },
    // A length of time the way a hotel says it: "18 min" under the hour, "1 h 05" over
    // it. The minutes are padded, because "1 h 5" reads as an hour and five hours.
    duration: {
        minutes: '{minutes} min',
        hours:   '{hours} h {minutes}',
    },
    // Maintenance: what is broken and who is mending it.
    maintenance: {
        title:              'Maintenance',
        room_block_failed:  'The room block failed - the room is still for sale. The system retries this automatically.',
        description:        'What is broken and who is mending it.',
        report:             'Report a fault',
        reported:           'The fault has been reported.',
        fault:              'Fault',
        area:               'Part of the building',
        area_hint:          'When it is not a room — the lift, the kitchen, the car park …',
        room_hint:          'Leave empty when the fault is not in a room.',
        no_room:            'No room',
        description_label:  'Description',
        severity:           'Severity',
        severity_blocking:  'Blocks the room',
        severity_normal:    'Normal',
        severity_cosmetic:  'Cosmetic',
        status_open:        'Reported',
        status_assigned:    'Assigned',
        status_in_progress: 'In progress',
        status_resolved:    'Repaired',
        status_closed:      'Closed',
        status_cancelled:   'Cancelled',
        status_duplicate:   'Duplicate',
        start:              'Take it',
        started:            'The fault has been taken up.',
        resolve:            'Repaired',
        resolved:           'The fault is marked as repaired.',
        resolution:         'What was done',
        resolution_hint:    'A short note for whoever sees the same fault six months from now.',
        nobody:             'Nobody',
        empty:              'No open faults.',
        filter_mine:        'Mine',
        filter_unassigned:  'Unassigned',
        filter_open:        'All open',
        load_failed:        'The faults could not be loaded.',
        action_failed:      'That could not be done.',
    },
    room: {
        room: 'Room',
    },
    nav: {
        board: 'Overview',
        stock: 'Stock',
        lost_items: 'Lost & found',
        services:    'Services',
        maintenance: 'Maintenance',
        logout:       'Log out',
        theme:        'Theme',
        theme_light:  'Light',
        theme_dark:   'Dark',
        theme_system: 'System',
        language:     'Language',
        language_sl:  'Slovenian',
        language_en:  'English',
        today:        'My tasks',
        sheet:        'Schedule',
        teams:        'Teams',
        statistics:   'Statistics',
        my_rota:      'My rota',
        my_time:      'My clock',
    },
    rota: {
        title:         'My rota',
        previous_week: 'Previous week',
        this_week:     'This week',
        next_week:     'Next week',
        today:         'today',
        free_day:      'day off',
        break_of:      '{minutes} break',
        unit_hours:    'h',
        unit_minutes:  'min',

        empty:      'You have no published shifts this week',
        empty_hint: 'They appear here once your manager publishes the week.',

        changes_title:    'Shift changes ({count})',
        pending_title:    'Requests waiting for a decision: {count}',
        change_moved:     'The shift {previous} is now {current}.',
        change_withdrawn: 'The shift {previous} has been withdrawn.',
        change_reason:    'Reason: {reason}',
        change_changed_at: 'Changed {at}',

        acknowledge:        'I understand',
        acknowledge_hint:   'Acknowledging says you have seen the change. Until you do, your manager sees that you do not know yet.',
        acknowledged:       'Acknowledged.',
        acknowledge_failed: 'The acknowledgement could not be recorded.',

        decline:        'I cannot',
        decline_title:  'You cannot work this shift?',
        decline_hint:   'Your manager will see that you cannot and will arrange cover. The change stays on the list until that is settled.',
        decline_note:   'Reason (optional)',
        decline_send:   'Send',
        declined:       'Recorded: you cannot work it.',
        decline_failed: 'The answer could not be recorded.',

        state_draft:     'Draft',
        state_published: 'Published',
        state_cancelled: 'Withdrawn',
    },
    // The working-time record (docs/OSEBJE.md §8): one's own clock and the corridor kiosk.
    staff_time: {
        today_total:        'Today',
        of_which_break:     'of which break {minutes}',
        break_counts_hint:  'The break counts as working time and is never deducted.',
        first_in:           'arrived {at}',
        last_out:           'left {at}',
        day_open:           'The day is unfinished — no departure recorded.',
        this_week:          'This week',
        no_days:            'No days recorded this week yet.',
        open:               'Unfinished',
        stamped:            'Recorded: {kind}',
        queued:             'Waiting for a connection: {count}',
        refused:            'The stamp was not accepted.',
        not_recorded:       'The stamp was not recorded: {reason}',

        kind_clock_in:      'Arrival',
        kind_clock_out:     'Departure',
        kind_break_start:   'Break',
        kind_break_end:     'End of break',

        kiosk_title:        'Clock',
        kiosk_pair_hint:    'Type the pairing code reception shows (Settings → Working-time record). It is worth five minutes and one attempt.',
        kiosk_code:         'Code',
        kiosk_pair:         'Pair this tablet',
        kiosk_code_invalid: 'This code is no longer valid. Ask for a new one.',
        kiosk_enter_pin:    'Enter your PIN',
        kiosk_backspace:    'Delete the last digit',
        kiosk_clear:        'Clear',
        kiosk_today:        'today {total}',
        kiosk_queued_title: 'Recorded at {at}',
        kiosk_queued_note:  'No connection — the stamp goes when it returns. The moment of the press is what is recorded.',
    },

    staff_vacation: {
        // The leave balance on "Request leave" — carried-over and current-year leave,
        // ZDR-1 article 162. Read only; the decision and the rules are the reception
        // portal's.
        days_count:       '{count} days',
        available:        'Available',
        old_leave:        'Carried-over leave ({year})',
        current_leave:    'Annual leave {year}',
        still_days:       '{days} left',
        still_of:         '{left} left of {total}',
        use_by:           'Use it by {date}.',
        expired_days:     '{days} lapsed \u2014 the deadline was {date}.',
        of_which_pending: 'Of which awaiting a decision: {days}',
        not_issued_hint:  'No decision has been issued for this year yet.',
        bucket_old:       'Carried over',
        bucket_current:   'This year',
        usage_history:    'How it was used',
        no_usage:         'No leave has been recorded for this year.',
        requested_days:   'Working days requested: {days}',
        over_remaining:   'This is more than the {days} available. The decision is still the house\u2019s.',
    },
    staff_absence: {
        // The kinds — OUR list, and provisional (docs/OSEBJE.md §10). Only leave may be
        // asked for; the rest reach the record by other roads.
        kind_vacation:      'Leave',
        kind_sick_leave:    'Sick leave',
        kind_holiday:       'Public holiday',
        kind_training:      'Training',
        kind_business_trip: 'Business trip',
        kind_unexcused:     'Unexcused absence',
        kind_other:         'Other',

        state_requested:    'Requested',
        state_approved:     'Approved',
        state_rejected:     'Rejected',
        state_withdrawn:    'Withdrawn',

        my_absences:        'My absences',
        no_my_requests:     'You have no requests for absence.',
        request_leave:      'Request leave',
        request_hint:       'You may ask for leave. Sick leave is reported and recorded, a public holiday comes off the calendar, and training and business trips are entered by a manager.',
        request_sent:       'The request has been sent for a decision.',
        send_request:       'Send request',
        decided_by_other:   'Somebody else decides your request — you cannot approve your own.',
        withdraw:           'Withdraw',
        withdrawn:          'The request has been withdrawn.',
        from:               'From',
        to:                 'To',
        note:               'Note',
        range_backwards:    'The end cannot be before the start.',
        range_too_long:     'A request may cover at most {days} days. A longer absence is entered by a manager.',
    },
    login: {
        demo_signing_in: 'Signing in …',
        demo_failed: 'This server has no such role.',
        title:    'Sign in',
        subtitle: 'Portal for housekeepers and the supervisor',
        username: 'Username',
        password: 'Password',
        submit:   'Sign in',
        failed:   'Wrong username or password',
    },
    property: {
        property:   'Property',
        properties: 'Properties',

        unavailable_title:       'The property is not known',
        unavailable_description: 'This account cannot read the list of properties (it is missing property:read), so the portal does not know which house it is in. Ask an administrator.',

        rooms_restricted_title:       'Room numbers are unavailable',
        rooms_restricted_description: 'Some tasks carry no room number. The work is shown, but the room has to be looked up.',
    },
    today: {
        awaiting_inspection: 'To inspect',
        awaiting_description: 'Finished rooms waiting for somebody to accept them. The longest wait is first.',
        awaiting_me:         'Waiting for you to look',
        waiting_for:         'waiting {value}',
        // "My tasks" rather than "Today": the screen is not a day, it is what this
        // person has in front of her — her rooms, the free ones, and (for a
        // supervisor) the ones waiting to be looked at. The business date stays in
        // the sub-line, where it always was.
        title:    'My tasks',
        subtitle: 'Rooms for today’s business date',

        progress: 'Finished',

        my_rooms:               'My rooms',
        // Why the list is not by floor: the order IS the round the supervisor arranged
        // (docs/HOUSEKEEPING.md §5.1.4), so a third-floor room can come before a
        // first-floor one. Without this line that reads as a fault.
        my_rooms_description:   'In the order to walk them — the floor is beside the door number.',
        unassigned_rooms:       'Unassigned rooms',
        unassigned_description: 'These rooms are not on anybody’s round yet. Press Take over to take one.',

        floor_unknown: 'Unknown floor',

        empty: 'There are no rooms for you today.',
    },
    sheet: {
        title:    'Schedule',
        subtitle: 'Every room of today’s business date',

        generate:         'Generate the schedule',
        generate_confirm: 'Generate the tasks for today’s business date. Tasks that already exist are left untouched.',
        generate_failed:  'The schedule could not be generated.',
        generated_message: 'Tasks created: {count}',

        filter_status: 'Status',
        filter_kind:   'Kind of work',
        all_statuses:  'All statuses',
        all_kinds:     'All kinds of work',
        floor_rooms:    '{count} room | {count} rooms',
        floor_areas:    '{count} area | {count} areas',
        floor_open:     '{count} to do',
        // Assignment is a different axis from status: a waiting room can be assigned
        // and one under way always is. It counts the same set "to do" counts.
        floor_unassigned:   '{count} unassigned',
        floor_all_assigned: 'all assigned',
        person_summary: 'still to do: {open} of {total}',
        // The box that is neither a status nor a filter — how much open work has a name.
        assigned_count: 'Assigned',
        group_by_floor:  'By floor',
        // The stored value stays 'person'; the label is "By executor", because an
        // executor is a person OR a team (docs/HOUSEKEEPING.md §15).
        group_by_person:   'By person',
        group_by_executor: 'By executor',
        round_ends: 'ends at {time}',

        empty: 'There are no tasks for this day. If the night audit has not run, press Generate the schedule.',

        assign_restricted_title:       'Assignment is unavailable',
        assign_restricted_description: 'This account cannot read this property\'s housekeepers (it is missing housekeeping:inspect), so rooms cannot be assigned.',
    },
    timeline: {
        search: 'Room or person',
        no_match: 'Nothing matches that search.',
        title:       'The day in order',
        description: 'When each room came into work, who took it and made it up, and who accepted it.',
        empty:       'Nothing has happened yet today.',
        started:     'taken for cleaning',
        finished:    'made up',
        skipped:     'skipped',
        inspected:   'accepted',
        days_today: 'Today',
        days_3: 'Last 3 days',
        days_7: 'Last 7 days',
        days_14: 'Last 14 days',
    },
    attention: {
        title: 'Room notes',
        add: 'Add a note',
        add_one: 'Add to the list',
        on_this_day: 'Written for today',
        what: 'What',
        value: 'Detail',
        value_hour: 'Hour',
        note: 'Note',
        from_reservation: 'from the booking',
        from_guest: 'from the guest profile',
        from_task: 'written today',
        vip: 'VIP',
        cot: 'Cot',
        extra_bed: 'Extra bed',
        pet: 'Pet',
        allergy: 'Allergy',
        products: 'Special products',
        extra_towels: 'Extra towels',
        extra_pillows: 'Extra pillows',
        clean_at: 'Clean at',
        do_not_enter_before: 'Do not enter before',
        language: 'Guest language',
        late_checkout: 'Late checkout',
        early_checkin: 'Early check-in',
        preferred_room: "The guest's own room",
    },
    // "Označi za čiščenje" — a room the calendar says nothing about, onto today's round
    // at once (docs/HOUSEKEEPING.md §17).
    mark: {
        title: 'Mark for cleaning',
        room: 'Room',
        room_placeholder: 'Pick a room',
        room_named: 'Room {room}',
        note_placeholder: 'E.g. the guest spilled wine, room shown at eleven …',
        submit: 'Mark for cleaning',
        marked_message: 'Room {room} is on today\'s round.',
        reason_guest_request: 'The guest asked',
        reason_complaint: 'A complaint about the room',
        reason_soiled: 'Soiled after cleaning',
        reason_after_use: 'After a viewing or work in the room',
        already_open_title: 'This room is already on today\'s round',
        open_task: 'Open the task',
        nobody: 'unassigned',
        due_by: 'by {hour}',
        refused_out_of_service: 'The room is out of sale. Put it back in use first, and it can then go on the round.',
        failed: 'The room could not be marked for cleaning.',
    },
    history: {
        title: 'What happened here today',
        hint: 'Every step, in the order it happened.',
        generated: 'Put on the sheet',
        assigned: 'Assigned',
        reassigned: 'Handed on',
        started: 'Started',
        deferred: 'Put off',
        resumed: 'Picked up again',
        finished: 'Finished',
        inspection_failed: 'Sent back',
        rework_started: 'Being put right',
        finished_again: 'Finished again',
        inspected: 'Accepted',
        skipped: 'Not cleaned today',
        marked_for_cleaning: 'Marked for cleaning',
        prioritised: 'Put first',
        prioritised_by_guest: 'The guest asked for the room — put first',
        deprioritised: 'Priority removed',
        to_person: 'to {name}',
        somebody: 'somebody else',
        by_the_hotel: 'the hotel',
    },
    linen: {
        title: 'Linen',
        card_hint: 'How much clean stayed in the room and how much dirty you collected.',
        hint: 'Every piece counts — towels, bed linen, bathrobes, slippers. Clean goes from the trolley into the room, dirty from the room into the basket.',
        open: 'Record linen',
        trolley: 'Your trolley',
        basket: 'Dirty basket',
        laundry: 'Laundry',
        laundry_title: 'Laundry',
        laundry_hint: 'How much dirty you handed to the laundry and how much clean you took onto the trolley.',
        left: 'Left clean',
        took: 'Took dirty',
        dropped: 'Handed dirty',
        taken: 'Took clean',
        nothing: 'Enter at least one quantity.',
        needs_places: 'Pick the trolley, the basket and the laundry.',
        saved: 'Linen recorded.',
        save_failed: 'Some entries could not be recorded — check the balances on Stock.',
        route_dirty: 'Dirty goes: {from} → {to}.',
        route_clean: 'Clean comes: {from} → {to}.',
        same_place: 'The basket and the laundry are the same place — the dirty is already there, enter only the clean you took.',
        rooms_missing: 'The desk should add a stock place of the kind "In the rooms". Without it there is nowhere to record clean linen that stays in a room.',
        no_linen: 'The hotel has no linen items. The desk adds them under stock items.',
    },
    stock: {
        title: 'Stock',
        hint: 'What is on the trolley and in the floor cupboards.',
        short_title: 'Running out',
        short_hint: 'Only what can actually be put on a bed today — towels in the wash are not towels.',
        empty: 'Nothing in stock.',
        every_place: 'Everywhere',
        move: 'Move',
        item: 'What',
        what_happened: 'What happened',
        quantity: 'How many',
        // A typed quantity crosses the wire as an integer in 10^decimals of the
        // article's unit (@/libs/quantity). The hint shows only where an article is
        // counted in something finer than whole pieces.
        quantity_invalid: 'Invalid quantity.',
        quantity_step: 'Enter to the nearest {step} ({unit}). Comma or full stop.',
        from: 'From',
        to: 'To',
        room: 'Room',
        room_hint: 'The item goes on the bill of the guest in this room.',
        note: 'Note',
        needs_item: 'Pick a thing and a quantity.',
        save_failed: 'The movement could not be recorded.',
        load_failed: 'Stock could not be loaded.',
        charged: 'Recorded and charged to the room.',
        gone_sold: '→ Minibar — charged to the room',
        gone_used: '→ Used up',
        gone_damaged: '→ Damaged (write-off)',
        hint_soiled: 'The linen goes to the dirty side — recorded as "to the laundry".',
        hint_washed: 'Clean linen coming back — recorded as "from the laundry".',
        hint_sold: 'The item goes onto the guest bill of the chosen room.',
        hint_used: 'The item is used up and leaves stock.',
        hint_damaged: 'The item is written off and leaves stock.',
        reason_transfer: 'Transfer',
        reason_used: 'Used up',
        reason_sold: 'Minibar — charged to the room',
        reason_soiled: 'To the laundry',
        reason_washed: 'Back from the laundry',
        reason_damaged: 'Damaged',
        reason_delivery: 'Delivery',
        reason_count: 'Stocktake',
    },
    // Teams: two or three housekeepers on one room (docs/HOUSEKEEPING.md §15).
    team: {
        section:      'Teams',
        section_hint: 'Two or three housekeepers on one room. A team belongs to one day; tomorrow it is offered back as one press.',
        pick_members: 'Pick at least two people',
        form:         'Form a team',
        formed:       '{name} is formed.',
        dissolve:     'Dissolve the team',
        open_rooms:   '{count} rooms left',
        unnamed:      'Team',
        with:         'with: {names}',
        factor_share: '{percent} % of the norm',

        // The Teams tab (v1.410.0). Forming moved here out of the round proposal: a
        // team is asked about all morning, and the proposal is pressed once.
        title:      'Teams',
        subtitle:   "This morning's teams",
        page_hint:  'A team belongs to one business day. Its name and its members can be changed; tomorrow both are offered back as one press.',
        suggestions: 'They worked together yesterday',
        empty:      'No team has been formed today. Use the button above, or pick one of yesterday\'s pairings.',
        edit:       'Edit the team',
        saved:      '{name} has been saved.',

        name:         'Team name',
        name_hint:    'What the sheet calls it and what somebody says out loud in a corridor. At most 40 characters, and unique for the day.',
        members:      'Members',
        members_hint: 'At least two. Whoever is in a team gets no rooms of her own that day.',
        lead:         'Lead',
        lead_hint:    'Optional. The lead is the person a room is written to, so that screens written before teams existed still show a name on it.',
        no_lead:      'No lead',

        // The intersection of the shifts: two eight-hour shifts that never meet are not
        // a team.
        no_common_shift:      'The shifts do not overlap — the team would have no time together',
        no_common_shift_hint: 'The rota does not have them in the house at the same time. The team can still be saved — a rota is a plan and the corridor is the fact — but the round will have no hours to give it.',
        common_shift:         '{value} together',
        common_shift_window:  'Together {window} · {value}',
        shift_unknown:        'The rota says nothing about this team',

        rooms_today: '{done}/{total} rooms today',
        open_sheet:  'Show on the sheet',

        // The round proposal: teams are only read there.
        working_today: 'The teams working today. Each is an executor of the split — they are formed and dissolved on the Teams tab.',
        manage:        'Manage teams',
        none_today:    'No team today.',

        create_failed:   'The team could not be formed.',
        save_failed:     'The team could not be saved.',
        name_taken:      'A team of this morning is already called that. Pick another name.',
        dissolve_failed: 'The team could not be dissolved. If it still holds rooms, move those off it first.',
        dissolve_open_rooms: 'The team still holds {count} open rooms. Move those off first — they are not handed back to the lead on their own.',

        // The two ends of one rule: a person is in a team OR holds rooms alone.
        conflict_in_team: '{name} is already in {team} today.',
        conflict_solo:    '{name} is still holding rooms alone today: {rooms}. Move those off first.',
        refused:          'The team was not formed',

        // The way out of decision 4's refusal: work that has not been started goes back
        // to nobody and the person joins the team. A room she is standing in stays hers.
        solo_work:        'has work of her own ({rooms})',
        solo_in_progress: 'She is in {rooms} right now — the team can be saved once she has finished it.',
        release_and_add:  'Release the work and add to the team',
        release_confirm:  '{rooms} go back to nobody and {name} joins the team. Go ahead?',
        released:         'Released: {rooms}',
        release_kept:     '{name} is still in {rooms}. The team can only be saved once that room is finished.',
        release_failed:   'The work could not be released.',
    },
    plan: {
        title: 'Propose a round',
        hint: 'The system proposes and you decide. It adds up the norms, splits the floors evenly and puts the rooms with an hour to meet first.',
        people: 'Who is working',
        people_hint: 'Who is on today — nobody but you knows that.',
        shift: 'Shift length (hours)',
        shift_hint: 'How long each of them has. It is what makes "this morning does not fit" visible.',
        propose: 'Propose',
        summary: '{work} of work, {capacity} of capacity.',
        nothing: 'nothing',
        unnamed: 'unnamed',
        left_over_title: 'Nobody to do it: {count}',
        apply: 'Apply the round',
        applied: '{count} rooms assigned.',
        // One line rather than a stack: fifteen calls with ten refusals used to be ten
        // identical toasts on top of the success. The reasons are grouped under it.
        applied_partial: 'Rooms assigned: {done} · could not be: {failed}',
        refused_group: '{rooms} — {reason}',
        no_executor: 'the round names nobody',
        needs_people: 'Pick at least one person.',
        failed: 'The proposal could not be prepared.',
        floor_action: 'Distribute this floor',
        // With nothing unassigned left on the floor the button is no longer the next
        // step but a decision, so it steps back and renames itself.
        floor_action_again: 'Reassign',
        floor_hint: '{floor}: every room on it is shared out evenly between the people you pick. No shift ceiling — no room is left out.',
        floor_work: 'Rooms on this floor: {rooms} · {work} of work.',
        include_assigned: 'Redistribute rooms already handed out (not started)',
        include_assigned_hint: 'Rooms somebody already holds but has not started go into the split too. A room being cleaned right now stays with her.',
        moves: 'Rooms that move: {moved} · staying where they are: {kept}.',
        kept: 'Stays with the same person',
        kept_count: 'staying put: {count}',
        kept_legend: 'A room with a lock stays with the same person — the work is already under way. Its minutes still count towards the total.',
        nothing_to_move: 'No room needs to move.',

        // The teams the proposal puts together itself (docs/HOUSEKEEPING.md §15, phase 4).
        form_teams: 'Form teams for rooms that recommend two or three',
        form_teams_hint: 'For rooms marked as worth a bigger team, the proposal pairs people itself — preferring the ones who worked together yesterday. Anybody in a team gets no rooms of her own. The teams are only created when you press Apply.',
        proposed: 'proposed',
        formed_for: 'proposed for {rooms} — recommended size {size}',
        formed_yesterday: 'they worked together yesterday',
        drop_team: 'Without this team',
        shortfall_line: '{rooms} recommends a team of {size} and {available} people are free. Lower the recommendation, call somebody in, or switch the team forming off.',
    },
    forecast: {
        title: 'Tomorrow',
        hint: "What tomorrow holds at this property's own norms, including the common areas due that day.",
        work: 'Expected work',
        rooms: 'Rooms',
        rooms_split: '{departures} departures · {stayovers} stayovers · {arrivals} arrivals',
        people: 'People needed',
    },
    live: {
        window: 'window {minutes} min',
        temperature: '{value}°',
        dnd: 'DO NOT DISTURB',
        mur: 'MAKE UP ROOM',
        silent: 'no reading',
    },
    task: {
        retry_hour: 'At an hour',
        retry_at_hour: 'Hour',
        kind_area_description:  'A common area on the property schedule — not a guest room.',
        kind_area:              'Common area',
        status_deferred: 'Deferred',
        status_rework: 'Needs re-cleaning',
        defer: 'Defer',
        deferred_message: 'The room has been put off until later.',
        deferred_message_area: 'The area has been put off until later.',
        deferred_no_hour: 'deferred',
        retry_when: 'When to try again',
        retry_30: '30 min',
        retry_60: '1 hour',
        retry_120: '2 hours',
        retry_at: 'again at {value}',
        retry_overdue: 'overdue since {value}',
        reason: 'Reason',
        reason_note: 'Note',
        reason_note_placeholder: 'For example: the guest asked us to come after 3 p.m.',
        reason_guest_present: 'Guest in the room',
        reason_other: 'Other',
        rework: 'Send back',
        rework_message: 'The room has been sent back for cleaning.',
        rework_message_area: 'The area has been sent back for cleaning.',
        rework_consequence: 'The room goes back to “to prepare” and cannot be sold until it has been cleaned again and accepted.',
        rework_consequence_area: 'The area goes back to “to prepare” and counts as not in order until it has been cleaned again and accepted.',
        rework_reason: 'What is wrong',
        rework_note: 'What needs putting right',
        rework_note_placeholder: 'For example: shower not dried, two towels missing.',
        rework_due_by: 'Right by',
        rework_due_by_hint: 'The hour the room has to be ready by. Empty means no deadline.',
        rework_due_by_hint_area: 'The hour the area has to be in order by. Empty means no deadline.',
        rework_bathroom: 'Bathroom',
        rework_beds: 'Beds',
        rework_supplies: 'Missing amenities',
        rework_missed: 'Room was not cleaned',
        rework_damage: 'Damage or stain',
        status_pending:     'Waiting',
        // Checking out and releasing the room are two events (docs/HOUSEKEEPING.md §16).
        status_awaiting_release: 'Waiting for the room',
        awaiting_release_hint:   'The guest has checked out and has not given the room back yet. It becomes work when somebody reports the room empty - the desk, the key, or the guest\'s own screen.',
        awaiting_release_refused: 'The room is not free yet - the guest has checked out and has not left it. It becomes work when somebody reports the room empty.',
        status_in_progress: 'In progress',
        status_done:        'Finished',
        status_inspected:   'Inspected',
        status_skipped:     'Skipped',

        kind_departure: 'Departure',
        kind_stayover:  'Stayover',
        kind_arrival:   'Arrival',
        kind_dirty:             'To prepare',
        kind_dirty_done:        'Prepared',

        kind_departure_description: 'The guest has left. Strip and make up the whole room: bed, bathroom and supplies.',
        kind_stayover_description:  'The guest is staying. Daily service around their own things — bed, bathroom, bins, towels.',
        kind_arrival_description:   'The room is empty and a guest arrives today. Prepare it and look it over before they walk in.',
        kind_dirty_description: 'The room is waiting to be prepared although nobody stayed and nobody is arriving today. Take it all the way.',

        consequence_done:           'The room is cleaned. Once a supervisor accepts it, it is ready for the next guest.',
        consequence_inspected:      'The room is accepted and ready for the next guest.',
        consequence_stayover_done:  'The daily service is done, but the room stays occupied — it is not ready for a new guest.',
        guest_still_in_room:        'The guest is still in the room',

        room_unknown: 'Unknown room',
        floor:         'Floor',
        business_date: 'Business date',
        started_at:    'Started',
        finished_at:   'Finished',
        inspected_at:  'Inspected',
        notes:         'Note',

        // The norm is a planning aid and not a mark against anybody. Nothing here is
        // worded as a reprimand: a room past its norm is a room the norm did not
        // describe, which is as often the room's doing as the housekeeper's.
        norm:       'Norm',
        norm_short: 'norm {value}',
        // A team's norm is a share of the room's. Without the other half of the
        // sentence, "33 min" under a ninety-minute apartment reads as a mistake.
        norm_team:  '(team of {size} · {solo} for one)',
        remaining:              '{value} left',
        over_by:                '{value} over',
        over_norm:  '{value} over the norm',
        elapsed:    'Running',
        duration:   'Took',

        unassigned:        'Unassigned',
        worked_by:              'Cleaned by {name}',
        awaiting_inspection:    'Waiting to be checked',

        stay_changed: 'The booking changed after this room was started',
        stay_gone: 'The booking this room was on the list for has been cancelled or moved. The work is still yours to finish or to skip — a supervisor decides.',
        stay_now_kind: 'The calendar now says this room needs: {kind}. What you were given has not been changed; tell your supervisor.',
        stay_now_due: 'The guest is now expected at {time}, not at the hour on this task.',
        stay_no_longer_due: 'The hour this room was being hurried for no longer applies.',
        due_by:                 'by {time}',

        priority:               'Priority',

        // What the button says, and both halves of the same button: the room that is
        // already first is the room somebody takes off again. "Do this first" and not
        // "Priority" — the button names the act and the badge names the state, and one
        // word for both would leave the reader working out which of the two it is.
        priority_set:   'Do this first',
        priority_clear: 'Remove priority',
        priority_hint:  'The room goes to the top of today\'s round.',
        priority_set_message:     'The room is at the top of today\'s round',
        priority_cleared_message: 'The room is back in the round\'s own order',

        assigned_to:       'Assigned to',
        assigned_to_you:   'You',
        assigned_to_other: 'Somebody else',

        assign:           'Assign',
        assign_to:        'Assign to',
        not_on_rota: "not on today's rota",

        // Who is already in a team today — the note beside a name in all three pickers,
        // right after the rota: "Marija Sobarica · 06:00–14:00 · in Ekipa A". The
        // service refuses such a person everywhere (she is in a team OR works alone),
        // so it is not a warning but a reason: the name is greyed and cannot be picked.
        in_team:         'in {name}',
        already_in_team: 'already in {name}',

        // The rostered hours under an executor's name. For a team it is the
        // intersection of its members' shifts — the window in which it is a team at all.
        no_shift:    'no rostered shift',
        assigned_message: 'The room has been assigned',
        assigned_message_area: 'The area has been assigned',

        actions: 'Actions',

        start:            'Start',

        // "Take over" rather than "Start" wherever the room is not already hers:
        // pressing it puts the room on whoever pressed it, and that is what the word
        // has to say before the press rather than after. Both the sheet row and the
        // task's own page now ask the same question of the same helper.
        take_over:        'Take over',

        // A room somebody else is in. In place of the button, not beside it.
        in_progress_by:   'working · {name}',
        started_message:  'The room has been started',
        started_message_area: 'The area has been started',

        // Taking somebody else's room. It says the thing the button does not: the room
        // becomes hers, and with it goes her own right to accept it.
        take_over_title:   'This room is on somebody else\'s round',
        take_over_title_area:   'This area is on somebody else\'s round',
        take_over_message: 'This room is on {name}\'s round. If you start it, the work will be recorded under your name — and a room you cleaned yourself cannot be accepted by you. Somebody else will have to accept it.',
        take_over_message_area: 'This area is on {name}\'s round. If you start it, the work will be recorded under your name — and an area you cleaned yourself cannot be accepted by you. Somebody else will have to accept it.',
        take_over_action:  'Take it and start',

        // The short question, for everybody else. The one above is the supervisor's and
        // says more — taking the room costs her the right to accept it — while this is
        // for the housekeeper nothing used to ask at all: another team's room changed
        // hands on a single press. The name is the executor's, so "Team B" on a team's
        // room.
        take_over_short_message:      'This room is assigned to {name} — take it over?',
        take_over_short_message_area: 'This area is assigned to {name} — take it over?',

        finish:           'Finish',
        finished_message: 'The room has been finished',
        finished_message_area: 'The area has been finished',

        finish_without_start:         'The work is already done',
        finish_without_start_confirm: 'This room has not been started. It will be recorded as started now and finished immediately, so the cleaning time will not be right. If you are only starting the work, press Start instead.',
        finish_without_start_confirm_area: 'This area has not been started. It will be recorded as started now and finished immediately, so the cleaning time will not be right. If you are only starting the work, press Start instead.',

        skip:            'Skip',
        skip_confirm:    'The room will not be cleaned. Write down the reason so the supervisor knows why.',
        skip_confirm_area:    'The area will not be cleaned. Write down the reason so the supervisor knows why.',
        skipped_message: 'The room has been skipped',
        skipped_message_area: 'The area has been skipped',

        inspect:           'Accept room',
        inspect_area:           'Accept area',
        inspect_confirm:   'This states that you have inspected the room and it is ready for a guest. It cannot be undone.',
        inspect_confirm_area:   'This states that you have inspected the area and it is in order. It cannot be undone.',
        inspected_message: 'The room has been accepted',
        inspected_message_area: 'The area has been accepted',

        note_title: 'Note',
        note_hint:  'What you write is sent together with Finish or Skip, and the supervisor reads it.',

        reason_do_not_disturb: 'Do not disturb',
        reason_guest_declined: 'The guest declined cleaning',
        reason_no_access:      'The room could not be accessed',

        not_found:  'The task could not be found.',
        no_actions: 'There is nothing you may do on this room.',
        no_actions_area: 'There is nothing you may do in this area.',

        closed_done:      'The room is cleaned and waiting for the supervisor to look at it.',
        closed_done_area:      'The area is cleaned and waiting for the supervisor to look at it.',
        closed_inspected: 'The room is accepted and ready. The work here is finished for today.',
        closed_inspected_area: 'The area is accepted and in order. The work here is finished for today.',
        closed_skipped:   'The room was skipped. There is nothing more to do here today.',
        closed_skipped_area:   'The area was skipped. There is nothing more to do here today.',

        load_failed:   'The tasks could not be loaded.',
        action_failed: 'The action could not be carried out.',
    },
    statistics: {
        rework: 'Re-cleaned',
        deferred: 'Deferred',
        of_which_dnd: 'of which {count} do-not-disturb',
        faults: 'Faults found',
        late: 'Late against the hour',
        late_of: '{late} of {total}',
        title:    'Statistics',
        subtitle: 'The work done, by person, by team, by kind and by day',

        period_today: 'Today',
        period_week:  '7 days',
        period_month: 'This month',

        range: '{from} to {to}',

        tasks:     'Rooms',
        done:      'Cleaned',
        inspected: 'Inspected',
        skipped:   'Skipped',

        average:    'Average per room',
        norm:       'Norm per room',
        norm_short: 'norm {value}',
        per_room:   'per room',
        worked:     'Time worked',
        // The denominator, said out loud: an average over four rooms and an average
        // over forty are different facts and must not be allowed to look alike.
        timed:      'Rooms timed',

        by_person:      'By person',
        by_person_hint: 'Work counts for whoever did it — whoever pressed Start. A team\'s room counts in full for every member, because they were all in it. Rooms nobody started are in the Unassigned row.',
        by_team:        'By team',
        by_team_hint:   'What the team did, and not the sum of two people: both of them were in every room. The norm is multiplied by the team factor.',
        by_kind:        'By kind of work',
        by_day:         'By day',

        // The team figures are a SUBSET of the rooms above them and never an addition:
        // an apartment two people cleaned is one room the house cleaned.
        team_work:        'In a team',
        in_team:          '{count} in a team',
        in_team_minutes:  '{value} in a team',
        efficiency:       'Against the norm',
        efficiency_short: '{percent} %',

        unassigned:     'Unassigned',
        person_unknown: 'Unknown person',
        team_unknown:   'Unknown team',

        empty:       'No work was recorded in this period.',
        load_failed: 'The statistics could not be loaded.',
    },
    service: {
        title: 'My services',
        description: 'The appointments you are giving today, in the order of the clock.',
        empty: 'Nothing booked for this day.',
        today: 'Today',
        tomorrow: 'Tomorrow',
        view_day: 'Day',
        view_week: 'Week',
        view_month: 'Month',
        includes_today: 'Includes today',
        period_count: 'Appointments: {count}',
        period_hours: '{hours} h in total',
        previous_day: 'Previous day',
        next_day: 'Next day',
        all_day: 'All day',
        days_count: '{count} days',
        hand_over: 'Hand over',
        handed_over: 'Handed over',
        handed_over_done: 'Handed over to the guest.',
        take_back: 'Take back',
        returned: 'Returned',
        returned_done: 'Recorded as returned.',
        to_hand_over: 'To hand over',
        hire_hours: 'Out {pickup} · back {back}',
        walk_in: 'No guest named',
        status_booked: 'Booked',
        status_in_progress: 'Under way',
        status_completed: 'Finished',
        start: 'Start',
        book: 'Book',
        booked: 'The appointment is booked',
        book_title: 'New appointment',
        book_service: 'Service',
        book_guest: 'Guest',
        book_guest_hint: 'The name as the guest gives it. May be left empty.',
        book_time: 'Free times',
        book_no_slots: 'Nothing free on this day.',
        book_failed: 'The appointment could not be booked.',
        started: 'The service is under way',
        finished: 'The service is finished',
        finish: 'Finish',
        finish_title: 'Finish the service',
        finish_question: 'How did the guest pay?',
        finish_free_hint: 'This service has no price — it is finished without a charge.',
        settle_folio: 'On the room',
        settle_folio_hint: 'It goes on the guest\'s card and is paid at check-out.',
        settle_folio_no_stay: 'Not possible — this appointment has no stay behind it.',
        settle_paid: 'Immediate payment',
        settle_paid_hint: 'Nothing goes on a card.',
    },
    license: {
        title:   'This service is not enabled',
        lead:    'Housekeeping and maintenance are not part of this hotel’s subscription, so this portal has nothing to show.',
        switch_account: 'Sign in with another account',
        contact: 'Contact your system administrator to have it switched on. The hotel’s data is untouched.',
    },
    errors: {
        operation_in_progress: 'This entry is already being recorded. Do not tap again \u2014 check the movements to see whether it landed.',
        operation_mismatch: 'This screen is stale: the same entry was started with different contents. Refresh and enter it again.',
        generic_error: 'An error occurred',
        invalid_data:  'Invalid data',
        not_found:     'Not found',
        unauthorized:  'You are not allowed to do that',
        staff_not_employed: 'You have no work period recorded for those days. Tell your supervisor — the personnel card is hers to fix.',
        staff_time_sequence: 'That stamp cannot follow the day as it stands (an arrival on a day that is already open, for instance).',
        staff_time_out_of_range: 'That moment is outside the window the record accepts.',
        staff_break_recording_off: 'This house does not record breaks.',
        staff_self_decision: 'You cannot decide your own request.',
        staff_pin_ambiguous: 'More than one employee has that PIN — your manager has to issue a new one.',
        staff_pin_throttled: 'Too many PIN attempts. Wait and try again.',
        group_hold_picked_up: 'That held room has already been picked up.',
        session_expired: 'The session has expired. Sign in again — nothing was done.',
        state_moved_on: 'Somebody else changed this room in the meantime. The screen has been refreshed — see where it is now and try again.',
        room_in_progress_by_other: 'Somebody else is cleaning this room.',
        conflict:      'The record already exists',
        on_add:        'Error while adding',
        on_load:       'The data could not be read',
        on_edit:       'Error while saving',
    },
    success: {
        add:  'Successfully added',
        edit: 'Successfully saved',
    },
    validation: {
        required: 'This field is required',
        email:    'Invalid email address',
    },
    // Milo, the same as at the front desk (docs/AI.md § "Milo v portalu sobaric"). The
    // texts are the reception portal's, except three: the greeting named the front desk,
    // the intro listed reservations and analytics, and the example questions were the
    // desk's - a supervisor pressing "What is the revenue this month?" would have been
    // refused a permission, and the first thing Milo said would have been "you may not".
    assistant: {
        history:           'Conversation history',
        history_search:    'Search titles…',
        new_conversation:  'New conversation',
        read_only:         'Another person\'s conversation - read only.',
        confirm:   'Confirm',
        decline:   'Decline',
        executed:  'Executed',
        op_failed: 'Failed',
        declined:  'Declined',
        already_decided: 'Already decided - this reached the proposal after it had already been settled.',
        title:        'Milo',
        greeting:     'I am Milo, the assistant on your floor.',
        intro:        'Ask anything about the house - rooms, the morning round, housekeepers, maintenance, stock. Milo reads live data; anything it would change, it proposes first and you confirm.',
        placeholder:  'Ask…',
        thinking:     'Milo is checking the data…',
        still_working: 'Milo is working… (this can take up to a minute)',
        failed:       'Milo could not answer. Try again.',
        // A failed turn is now drawn in the thread and not only as a toast (§20).
        // Its own sentence for the case where the service is simply not there - a
        // deploy, a restart, a gateway answering in its place - because then the
        // answer is to wait, not to reword; every other reason the server states.
        failed_unreachable: 'Milo could not answer: the server is not reachable right now - try again in half a minute.',
        failed_timeout:     'Milo took too long for this question. Ask a narrower one, or split it in two.',
        failed_lost:        'Milo was interrupted while answering (a service update). Send the question again.',
        retry:        'Send again',
        disclaimer:   'Milo reads live data; anything it would change, it proposes first and you confirm. Verify numbers on the proper screen before acting.',
        example_arrivals:     'Which rooms are not cleaned yet?',
        example_availability: 'Who is working today?',
        example_revenue:      'What is open in maintenance?',
        waits_for_step:       'waits for step {step}',
        step_prepared_below:  'Milo is preparing it below',
        continued:            'Step done - Milo is carrying on with the next one.',
        // The top-bar orb, the drawer and page awareness.
        shortcut:       'Alt+J',
        orb_attention:  'Something is waiting for you',
        // The poster's alternative text while the 3D head loads (MiloHead.vue).
        head_loading:   'Milo is loading…',
        on_page:        'On this page: {subject}',
        on_page_hint:   'Milo knows what you have open. Click if the question is not about it.',
        on_page_drop:   'Drop "{subject}" from the question',
        // Links in an answer and in the outcome line (libs/linkify.ts).
        copy_link:        'Copy link',
        link_copied:      'The link is on the clipboard.',
        link_copy_failed: 'Copying failed - select and copy the link by hand.',
    },
    // What an execution did. The server sends a code and a count (ChatOperationOutcome)
    // and the screen builds the sentence, because the server's own line is an English
    // audit record and not text for the staff. Codes that count nothing carry `other`
    // only.
    //
    // The whole block is copied from pms-web rather than trimmed: the code comes from a
    // server that does not know which portal is reading the answer, and a missing one
    // would mean the screen showing the English audit line instead.
    assistant_result: {
        nights_priced: {
            one:   'priced 1 night',
            other: 'priced {count} nights',
        },
        rooms_closed: {
            one:   'closed 1 room',
            two:   'closed {count} rooms',
            few:   'closed {count} rooms',
            other: 'closed {count} rooms',
        },
        rooms_reopened: {
            one:   'returned 1 room to sale',
            two:   'returned {count} rooms to sale',
            few:   'returned {count} rooms to sale',
            other: 'returned {count} rooms to sale',
        },
        guests_retagged: {
            one:   'updated the tags of 1 guest',
            other: 'updated the tags of {count} guests',
        },
        tasks_assigned: {
            one:   'assigned 1 task',
            two:   'assigned {count} tasks',
            few:   'assigned {count} tasks',
            other: 'assigned {count} tasks',
        },
        round_assigned: {
            one:   'assigned 1 room',
            two:   'assigned {count} rooms',
            few:   'assigned {count} rooms',
            other: 'assigned {count} rooms',
        },
        stays_moved: {
            one:   'moved 1 stay',
            two:   'moved {count} stays',
            few:   'moved {count} stays',
            other: 'moved {count} stays',
        },
        group_hold_moved: {
            other: 'moved the held room - {detail}',
        },
        group_hold_restructured: {
            other: "moved the held room and restructured the group's block - {detail}",
        },
        group_hold_released: {
            other: 'released the held room back to the shop; the agreed number is unchanged - {detail}',
        },
        group_room_picked_up: {
            other: 'picked the room up out of the group - {detail}',
        },
        group_shifted: {
            one:   'moved the group, 1 room shifted - {detail}',
            two:   'moved the group, {count} rooms shifted - {detail}',
            few:   'moved the group, {count} rooms shifted - {detail}',
            other: 'moved the group, {count} rooms shifted - {detail}',
        },
        group_dates_changed: {
            one:   "changed the group's dates, 1 room resized - {detail}",
            two:   "changed the group's dates, {count} rooms resized - {detail}",
            few:   "changed the group's dates, {count} rooms resized - {detail}",
            other: "changed the group's dates, {count} rooms resized - {detail}",
        },
        group_status_changed: {
            other: "changed the group's status - {detail}",
        },
        mappings_saved: {
            one:   'saved 1 mapping',
            two:   'saved {count} mappings',
            few:   'saved {count} mappings',
            other: 'saved {count} mappings',
        },
        program_proposed: {
            one:   'proposed 1 programme point for {detail}',
            two:   'proposed {count} programme points for {detail}',
            few:   'proposed {count} programme points for {detail}',
            other: 'proposed {count} programme points for {detail}',
        },
        display_config_saved: {
            other: 'saved the room screens configuration (revision {detail})',
        },
        channel_linked: {
            other: 'linked the channel booking to an existing reservation - {detail}',
        },
        channel_placed: {
            other: 'made reservation {detail}',
        },
        channel_replayed: {
            other: 'reservation {detail} was already made by an earlier press; no second one was created',
        },
        full_sync_accepted: {
            other: 'the full sync ({detail}) is accepted and runs in the background',
        },
        comfort_confirmed: {
            other: 'the equipment has confirmed it - room {detail}',
        },
        comfort_sent: {
            other: 'the order was sent to room {detail}; the equipment has not confirmed it yet',
        },
        comfort_unconfirmed: {
            other: 'the order went and is NOT confirmed - {detail}',
        },
        issue_reported: {
            other: 'raised a maintenance work order - {detail}',
        },
        issue_appended: {
            other: 'added to the open work order "{detail}"; no second one was raised',
        },
        issue_already_added: {
            other: 'the work order "{detail}" already carried this addition; nothing was written',
        },
        message_sent: {
            other: 'sent to {detail}',
        },
        checked_in: {
            one:   'checked in 1 room',
            two:   'checked in {count} rooms',
            few:   'checked in {count} rooms',
            other: 'checked in {count} rooms',
        },
        checked_out: {
            one:   'finalised the bill and checked out 1 room',
            two:   'finalised the bill and checked out {count} rooms',
            few:   'finalised the bill and checked out {count} rooms',
            other: 'finalised the bill and checked out {count} rooms',
        },
        room_prioritised: {
            other: 'room {detail} is at the top of today\'s round',
        },
        room_released: {
            other: 'room {detail} is free and waiting to be prepared',
        },
        reservation_created: {
            other: 'made the reservation - {detail}',
        },
        offer_created: {
            one:   'drafted the offer for 1 room - {detail}',
            two:   'drafted the offer for {count} rooms - {detail}',
            few:   'drafted the offer for {count} rooms - {detail}',
            other: 'drafted the offer for {count} rooms - {detail}',
        },
        offer_sent: {
            other: 'sent the offer - {detail}',
        },
        // Two codes and not one: "saved" over a record that already existed hides the
        // one thing worth reading — whether the hotel now has a SECOND account for one
        // legal entity. The correction counts fields, so it carries only `other`.
        company_created: {
            other: 'opened the corporate account - {detail}',
        },
        company_updated: {
            other: 'corrected the corporate account - {detail}',
        },
        // Counts FIELDS and not things, because a correction to a guest card may be one
        // telephone number or half the record, and somebody reading the thread a week
        // later wants to know how much moved. The guest's name is in {detail}.
        guest_updated: {
            one:   'corrected the guest card - 1 field changed - {detail}',
            two:   'corrected the guest card - {count} fields changed - {detail}',
            few:   'corrected the guest card - {count} fields changed - {detail}',
            other: 'corrected the guest card - {count} fields changed - {detail}',
        },
        // A discount is TWO codes and the difference is the whole answer: inside the
        // ceiling the money is already off, over it NOTHING is off and a request is
        // waiting. One line for both would be a price quoted to a guest that the house
        // has not agreed to.
        discount_applied: {
            other: 'discount applied - {detail}',
        },
        discount_pending: {
            other: 'the discount request is waiting for approval; nothing has come off the bill - {detail}',
        },
        line_removed: {
            other: 'line removed as a storno - {detail}',
        },
        line_moved: {
            other: 'line moved whole - {detail}',
        },
        line_split: {
            other: 'line split - {detail}',
        },
        overpayment_refunded: {
            other: 'overpayment refunded - {detail}',
        },
        payment_transferred: {
            other: 'payment moved between the cards; the house paid out nothing - {detail}',
        },
        absence_approved: {
            one:   'absence approved — 1 day written into the record · {detail}',
            two:   'absence approved — {count} days written into the record · {detail}',
            few:   'absence approved — {count} days written into the record · {detail}',
            other: 'absence approved — {count} days written into the record · {detail}',
        },
        absence_rejected: {
            other: 'absence rejected; no day was written into the record — {detail}',
        },
        correction_approved: {
            other: 'time correction approved — {detail}; the entry it replaces is marked corrected',
        },
        correction_rejected: {
            other: 'time correction rejected; the record is unchanged — {detail}',
        },
        notice_queued: {
            other: 'message queued — {detail}',
        },
        shift_changed: {
            other: 'shift moved — {detail}',
        },
    },
}
